import type React from 'react';
import { useMemo, useState } from 'react';
import { Button } from 'shared-portal-components-v2';

import { isValidLogLevel, LogLevel } from '@/types/Logger';
import type { LogEntry } from '@/types/LogEntry';

const LOG_LEVEL_OPTIONS: LogLevel[] = Object.values(LogLevel);
const HIDDEN_WHEN_DEBUG_OFF: LogLevel[] = [LogLevel.DEBUG, LogLevel.TRACE];

interface LogsTabProps {
  logEntries: LogEntry[];
  logNamespaces: string[];
  logLevel: LogLevel;
  onSetLogPattern: (input: { pattern: string; enabled: boolean }) => void;
  onSetLogLevel: (level: LogLevel) => void;
  onClearLogs: () => void;
}

export default function LogsTab({
  logEntries,
  logNamespaces,
  logLevel,
  onSetLogPattern,
  onSetLogLevel,
  onClearLogs,
}: LogsTabProps): React.JSX.Element {
  const [showDebug, setShowDebug] = useState(false);
  const [patternDraft, setPatternDraft] = useState('');

  const visibleEntries = useMemo(
    () =>
      showDebug
        ? logEntries
        : logEntries.filter((entry) => !HIDDEN_WHEN_DEBUG_OFF.includes(entry.level)),
    [logEntries, showDebug]
  );

  const handleAddPattern = (event: React.FormEvent): void => {
    event.preventDefault();
    const pattern = patternDraft.trim();
    if (pattern.length === 0) return;
    onSetLogPattern({ pattern, enabled: true });
    setPatternDraft('');
  };

  return (
    <div className="flex h-full flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-muted-foreground">
            Level
            <select
              value={logLevel}
              onChange={(event) => {
                const value = event.target.value;
                if (isValidLogLevel(value)) {
                  onSetLogLevel(value);
                }
              }}
              className="rounded border border-border bg-background px-2 py-1 text-foreground"
            >
              {LOG_LEVEL_OPTIONS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-1.5 text-muted-foreground">
            <input
              type="checkbox"
              checked={showDebug}
              onChange={(event) => {
                setShowDebug(event.target.checked);
              }}
            />
            Show debug
          </label>
        </div>

        <Button variant="secondary" size="md" onClick={onClearLogs}>
          Clear
        </Button>
      </div>

      <form onSubmit={handleAddPattern} className="flex items-center gap-2">
        <input
          type="text"
          value={patternDraft}
          onChange={(event) => {
            setPatternDraft(event.target.value);
          }}
          placeholder="namespace pattern (e.g. p2p:*)"
          aria-label="Add log namespace pattern"
          className="flex-1 rounded border border-border bg-background px-2 py-1 text-foreground"
        />
        <Button variant="primary" size="md" type="submit">
          Enable
        </Button>
      </form>

      {logNamespaces.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {logNamespaces.map((namespace) => (
            <button
              key={namespace}
              type="button"
              onClick={() => {
                onSetLogPattern({ pattern: namespace, enabled: false });
              }}
              aria-label={`Disable namespace ${namespace}`}
              className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-foreground hover:bg-muted"
            >
              {namespace} ✕
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto rounded border border-border bg-muted/20 font-mono text-xs">
        {visibleEntries.length === 0 ? (
          <p className="p-3 text-muted-foreground">No log entries captured.</p>
        ) : (
          <ul>
            {visibleEntries.map((entry, index) => (
              <li
                key={`${String(entry.timestamp)}-${String(index)}`}
                className="border-b border-border/50 px-3 py-1 break-all last:border-b-0"
              >
                <span className="text-muted-foreground">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>{' '}
                <span className="font-semibold">[{entry.level.toUpperCase()}]</span>{' '}
                <span className="text-muted-foreground">[{entry.namespace}]</span>{' '}
                <span className="text-foreground">{entry.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
